import { createContext, useContext, type ReactNode } from 'react';

/**
 * The form's own custom CSS, already sanitized server side.
 *
 * Custom questions render in a separate document, which inherits no styling
 *  from the page, so the CSS a business wrote for their form has to be handed
 *  in deliberately. That lets a rule overriding --breely-color on the form
 *  reach a custom question too, rather than leaving it the one element on the
 *  page their stylesheet can't touch.
 *
 * Only ever the sanitized copy. The raw text a business types is unscoped, and
 *  the same rule applies here as in CustomCssScope.
 */
const FormCustomCssContext = createContext<string>('');

export function useFormCustomCss(): string {
    return useContext(FormCustomCssContext);
}

/**
 * Passes straight through when there is no CSS, so a nested provider - ex. the
 *  designer, which themes each question separately inside one scope root -
 *  can't blank out what an outer one established.
 */
export const FormCustomCssProvider = ({
    css,
    children,
}: {
    css?: string | null;
    children: ReactNode;
}) => {
    if (!css) {
        return <>{children}</>;
    }

    return (
        <FormCustomCssContext.Provider value={css}>
            {children}
        </FormCustomCssContext.Provider>
    );
};

export default FormCustomCssContext;
